import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ar from './locales/ar.json';
import en from './locales/en.json';

/**
 * Arabic-first i18n. The product is RTL in Arabic, so Arabic is the default and
 * the fallback, not an afterthought.
 *
 * Every user-visible string must come from a locale file. There is no inline
 * default text in components, so a missing key fails visibly in development
 * instead of silently shipping English into an Arabic page.
 */

export const SUPPORTED_LOCALES = ['ar', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'ar';

const RTL_LOCALES: readonly string[] = ['ar', 'he', 'fa', 'ur'];

export function isRtl(locale: string): boolean {
  const base = locale.split('-')[0] ?? locale;
  return RTL_LOCALES.includes(base);
}

/** Keep `<html lang>` and `<html dir>` in step with the active locale. */
export function applyDocumentLocale(locale: string, doc: Document = document): void {
  doc.documentElement.lang = locale;
  doc.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

let initialised = false;

export function initI18n(locale: SupportedLocale = DEFAULT_LOCALE): typeof i18n {
  if (!initialised) {
    void i18n.use(initReactI18next).init({
      resources: {
        ar: { translation: ar },
        en: { translation: en },
      },
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      // A missing key is a bug, not something to paper over with English.
      parseMissingKeyHandler: (key: string) => `⟨missing:${key}⟩`,
      returnNull: false,
      interpolation: { escapeValue: false },
    });
    initialised = true;
  }
  if (typeof document !== 'undefined') {
    applyDocumentLocale(locale);
  }
  return i18n;
}

/** Read the browser's preferred locale, restricted to what we actually ship. */
export function detectLocale(navigatorLanguage?: string): SupportedLocale {
  const raw = navigatorLanguage ?? (typeof navigator === 'undefined' ? '' : navigator.language);
  const base = (raw.split('-')[0] ?? '').toLowerCase();
  return (SUPPORTED_LOCALES as readonly string[]).includes(base)
    ? (base as SupportedLocale)
    : DEFAULT_LOCALE;
}

export { i18n };
